import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'

import api from '../services/api'

import { Input } from './Form/Input'
import { Checkbox } from './Form/Checkbox'
import { TextArea } from './Form/TextArea'
import { Select } from './Form/Select'

import { LandingPageProcessDTO } from '../dtos/LandingPageProcessDTO'

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "video/mp4",
  "video/webm",
];


const contactUsFormSchema = z.object({
  name: z
    .string()
    .min(10, { message: 'O campo nome completo é obrigatório.' })
    .toUpperCase(),
  cellPhone: z.string().min(14, { message: 'O campo celular é obrigatório.' }),
  address: z.string().min(10, { message: 'O campo endereço é obrigatório.' }),
  district: z.string().min(10, { message: 'O campo bairro é obrigatório.' }),
  referencePoint: z.string().min(3, { message: 'O campo ponto de referência é obrigatório.' }),
  observation: z.string().min(10, { message: 'O campo descrição da situação é obrigatório.' }),
  isNearToWhereToReside: z.string().min(3, { message: 'O campo você mora perto é obrigatório.' }),
  dateObservation: z.string().min(10, { message: 'O campo data e hora da observação é obrigatório.' })
})

type ContactUsFormData = z.infer<typeof contactUsFormSchema>

interface FormContactUsProps {
  landingPageProcess: LandingPageProcessDTO
}

interface Contact {
  activityId: string,
  contactId: string
}

export function FormContactUs({ landingPageProcess }: FormContactUsProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState(false);
  const [checkedItems, setCheckedItems] = useState([true, true, true])
  const [toastOptions, setToastOptions] = useState(false)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<ContactUsFormData>({
    resolver: zodResolver(contactUsFormSchema),
  })

  const toast = useToast()
  const navigate = useNavigate()

  const allChecked = checkedItems.every(Boolean)
  const isIndeterminate = checkedItems.some(Boolean) && !allChecked

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFileError(false)

    if (!e.target.files || e.target.files.length === 0) {
      setFiles([]);
      return;
    }

    const selectedFiles = Array.from(e.target.files);
    
    // Validação de tipo
    const invalidTypes = selectedFiles.some(file => !ACCEPTED_FILE_TYPES.includes(file.type));
    if (invalidTypes) {
      toast({
        title: 'Apenas formatos de imagem e vídeo são aceitos.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
      setFileError(true)
      return;
    }
    
    // Validação de tamanho
    const invalidSizes = selectedFiles.some(file => file.size > MAX_FILE_SIZE);
    if (invalidSizes) {
      toast({
        title: 'Cada arquivo deve ter no máximo 25MB.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
      setFileError(true)
      return;
    }
    
    setFiles(selectedFiles);
  };

  async function handleContactUs(data: ContactUsFormData) {
    if (fileError) {
      return;
    }

    data.observation = `Endereço: ${data.address} <br />
    Bairro: ${data.district}  <br /> 
    Ponto de referência: ${data.referencePoint} <br />
    Você mora perto de onde está o animal: ${data.isNearToWhereToReside} <br />
    Data e hora da observação: ${data.dateObservation} <br />
    Descrição da situação: ${data.observation}`

    try {
      const { data: contactData } = await api.post<Contact>('contacts', {
        landingPageId: Number(landingPageProcess.id),
        name: data.name,
        cellPhone: data.cellPhone.replace(/\D/g, ''),
        acceptCommunicationEmail: checkedItems[0],
        acceptCommunicationSms: checkedItems[1],
        acceptCommunicationWhatsapp: checkedItems[2],
        observation: data.observation
      })

      const formData = new FormData();

      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      await api.post(`contacts/files/${contactData.contactId}/${contactData.activityId}`, 
        formData
      )

      setValue('name', '')
      setValue('cellPhone', '')
      setValue('observation', '')
      setFiles([])

      navigate('agradecimento')
    } catch (err) {
      toast({
        title: 'Error ao efetuar o cadastro!',
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  }

  return (
    <Flex
      w="100%"
      justify="center"
      align="center"
      gap="8"
      p="4"
      id="contact"
    >
      <Flex
        as="form"
        onSubmit={handleSubmit(handleContactUs)}
        maxW={1100}
        w="100%"
        align="center"
        justify="center"
        flexDirection="column"
        py="8"
        px="14"
        borderRadius={20}
        bgColor="gray.100"
        shadow="xl"
        encType="multipart/form-data"
      >
        <Text color={landingPageProcess.colorBase} textAlign="center" my="6">
          {landingPageProcess.titleForm}
        </Text>

        <Flex flexDirection="column" width="100%" gap="4">
          <Input
            label="(*) Nome Completo:"
            type="text"
            placeholder="Digite seu nome completo"
            {...register('name')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.name}
          />

          <Input
            as={InputMask}
            mask="(99)99999-9999"
            label="(*) Celular:"
            type="text"
            placeholder="Digite seu celular"
            {...register('cellPhone')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.cellPhone}
          />

          <Input
            label="(*) Endereço:"
            type="text"
            placeholder="Digite o endereço"
            {...register('address')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.address}
          />

          <Input
            label="(*) Bairro:"
            type="text"
            placeholder="Digite o bairro"
            {...register('district')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.district}
          />

          <Input
            label="(*) Ponto de referência:"
            type="text"
            placeholder="Digite o ponto de referência"
            {...register('referencePoint')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.referencePoint}
          />

          <Select
            label="(*) Você mora perto de onde está o animal:"
            placeholder="Selecione um opção"
            colorLabel={landingPageProcess.colorBase}
            options={[
              {
                value: "Não",
                label: "Não"
              },
              {
                value: "Sim",
                label: "Sim"
              }
            ]}
            {...register('isNearToWhereToReside')}
            error={errors.isNearToWhereToReside}
          />

          <Input
            label="(*) Data e hora da observação:"
            type="datetime-local"
            placeholder="Digite o ponto de referência"
            {...register('dateObservation')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.dateObservation}
          />

          <TextArea
            label="(*) Descrição da situação:"
            colorLabel={landingPageProcess.colorBase}
            error={errors.observation}
            {...register('observation')}
          />

          <Input
            label="Anexe fotos e/ou vídeos"
            type="file"
            name="documents"
            multiple
            accept="image/jpeg, image/jpg, image/png, image/webp, video/mp4, video/webm"
            colorLabel={landingPageProcess.colorBase}
            py="1"
            onChange={handleFileChange}
          />

          <Text w="100%" color="red" textAlign="left">
            (*) campos obrigatórios
          </Text>

          <Box>
            <Flex align="center" mt={1} mb={0}>
              <Checkbox
                isChecked={allChecked}
                isIndeterminate={isIndeterminate}
                label="Eu concordo em receber comunicações:"
                colorLabel={landingPageProcess.colorBase}
                name="allAccept"
                onChange={(e) =>
                  setCheckedItems([
                    e.target.checked,
                    e.target.checked,
                    e.target.checked,
                  ])
                }
              />

              <Button
                color={landingPageProcess.colorBase}
                _hover={{
                  bg: 'transparent',
                  color: landingPageProcess.colorHeader,
                }}
                onClick={() => setToastOptions(!toastOptions)}
                size="md"
              >
                ver opções
              </Button>
            </Flex>

            <Stack pl={6} spacing={2} display={toastOptions ? 'flex' : 'none'}>
              <Checkbox
                isChecked={checkedItems[0]}
                label="E-mail marketing."
                colorLabel={landingPageProcess.colorBase}
                name="acceptEmail"
                onChange={(e) =>
                  setCheckedItems([
                    e.target.checked,
                    checkedItems[1],
                    checkedItems[2],
                  ])
                }
              />
              <Checkbox
                isChecked={checkedItems[1]}
                label="SMS."
                colorLabel={landingPageProcess.colorBase}
                name="acceptSms"
                onChange={(e) =>
                  setCheckedItems([
                    checkedItems[0],
                    e.target.checked,
                    checkedItems[2],
                  ])
                }
              />
              <Checkbox
                isChecked={checkedItems[2]}
                label="WhatsApp."
                colorLabel={landingPageProcess.colorBase}
                name="acceptWhatsapp"
                onChange={(e) =>
                  setCheckedItems([
                    checkedItems[0],
                    checkedItems[1],
                    e.target.checked,
                  ])
                }
              />
            </Stack>
          </Box>

          {landingPageProcess.urlInstitutional !== '' && (
            <Text>
              Ao informar meus dados, eu concordo com a {` `}
              <Link
                href={landingPageProcess.urlPrivacy}
                color={landingPageProcess.colorBase}
                fontWeight="bold"
                target="_blank"
              >
                Política de Privacidade.
              </Link>
            </Text>
          )}
        </Flex>

        <Flex
          as={Button}
          type="submit"
          isLoading={isSubmitting}
          colorScheme="teal"
          variant="outline"
          spinnerPlacement="start"
          w="100%"
          maxW={250}
          h={50}
          bgColor={landingPageProcess.colorBase}
          color="#FFF"
          border="2px solid transparent"
          _hover={{
            borderColor: landingPageProcess.colorBase,
            bgColor: 'transparent',
            color: landingPageProcess.colorBase,
          }}
          fontWeight="bold"
          fontSize="20"
          borderRadius="12"
          justify="center"
          align="center"
          gap="2"
          my="8"
        >
          Cadastrar
        </Flex>
      </Flex>
    </Flex>
  )
}
